// Room-scoped voice coordination. Two concerns, both shared by every
// VoicePlayer inside one chat room:
//
//  1. Playback speed — pick 2× on one voice and every other voice in the same
//     room plays at 2× too. Resets to 1× when the room unmounts (you leave) or
//     `resetKey` (the ticketId) changes (switch conversations).
//
//  2. Auto-advance — when a voice finishes playing, the NEXT voice in the room
//     (by message order) starts automatically, chaining to the last one. Each
//     player registers a play() callback keyed by its message sequence; on a
//     natural end the provider finds the next-higher sequence and plays it.
//
// Why a provider: both behaviours need state shared across sibling players and
// scoped to one room. A provider mounted at the chat-room level gives that and
// resets cleanly on exit. No provider (e.g. the Bayon embed) → useVoiceRoom
// returns null and players fall back to standalone behaviour.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export interface VoiceRoomValue {
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
  /** Register a voice's play() callback under its message sequence. Returns an
   *  unregister fn for cleanup on unmount. */
  registerVoice: (seq: number, play: () => void) => () => void;
  /** A voice finished naturally — play the next-higher registered sequence. */
  playNext: (seq: number) => void;
}

const VoiceRoomCtx = createContext<VoiceRoomValue | null>(null);

export interface VoiceSpeedProviderProps {
  /** Reset shared speed to 1× whenever this changes (pass the ticketId). */
  resetKey?: string | number;
  children: ReactNode;
}

export function VoiceSpeedProvider({ resetKey, children }: VoiceSpeedProviderProps) {
  const [playbackRate, setPlaybackRate] = useState(1);

  // seq → play() for every voice currently mounted in the room.
  const voicesRef = useRef<Map<number, () => void>>(new Map());

  // Switching conversations (resetKey change) starts fresh at 1×. Leaving the
  // room entirely unmounts the provider, which also resets.
  useEffect(() => {
    setPlaybackRate(1);
  }, [resetKey]);

  const registerVoice = useCallback((seq: number, play: () => void) => {
    voicesRef.current.set(seq, play);
    return () => {
      voicesRef.current.delete(seq);
    };
  }, []);

  const playNext = useCallback((seq: number) => {
    const next = [...voicesRef.current.keys()]
      .filter((s) => s > seq)
      .sort((a, b) => a - b)[0];
    if (next !== undefined) voicesRef.current.get(next)?.();
  }, []);

  const value = useMemo<VoiceRoomValue>(
    () => ({ playbackRate, setPlaybackRate, registerVoice, playNext }),
    [playbackRate, registerVoice, playNext],
  );

  return <VoiceRoomCtx.Provider value={value}>{children}</VoiceRoomCtx.Provider>;
}

/** Shared room voice state, or null when rendered outside a provider. */
export function useVoiceRoom(): VoiceRoomValue | null {
  return useContext(VoiceRoomCtx);
}
